"use client";

// @ts-nocheck
import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { UmpireAssistantIcon } from "../icons/UmpireAssistantIcon";
import { ADRIcon } from "../icons/ADRIcon";
import { SettleFastIcon } from "../icons/SettleFastIcon";
import { AdminIcon } from "../icons/AdminIcon";
import GlobalAdminIcon from "../icons/GlobalAdminIcon";
import CompanyAdminIcon from "../icons/CompanyAdminIcon";
import BillingSettingsIcon from "../icons/BillingSettingsIcon";
import SupportIcon from "../icons/SupportIcon";
import FeedbackIcon from "../icons/FeedbackIcon";
import { DashboardIcon } from "../icons/DashboardIcon";
import { ProjectsIcon } from "../icons/ProjectsIcon";
import { TypeOfLossIcon } from "../icons/TypeOfLossIcon";
import { InvoiceActivityIcon } from "../icons/InvoiceActivityIcon";
import { SettingsIcon } from "../icons/SettingsIcon";
import { UsersIcon } from "../icons/UsersIcon";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import {
  ArrowLeftRight,
  BookOpen,
  Building2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileCog,
  FileText,
  Handshake,
  Home,
  Megaphone,
} from "lucide-react";

interface SidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
}

export default function Sidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}: SidebarProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const { t } = useI18n();
  const me = session?.user;
  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>({});
  const isSuperAdmin = Boolean(me?.isSuperAdmin);
  const isCompanyAdmin = Boolean(me?.isCompanyAdmin);
  const canManageCompanyBilling = Boolean(me?.canManageCompanyBilling);
  const orgId = me?.org_id ?? me?.sql_org_id ?? me?.orgId ?? null;
  const isIndividual = !orgId;

  const canAccessUA =
    isSuperAdmin || session?.user?.allowedModules?.includes("UA");
  const canAccessADR =
    isSuperAdmin || session?.user?.allowedModules?.includes("ADR");
  const canAccessCR =
    isSuperAdmin || session?.user?.allowedModules?.includes("CR");
  const canAccessSF =
    isSuperAdmin ||
    session?.user?.allowedModules?.includes("SF") ||
    session?.user?.allowedModules?.includes("SettleFast");
  const showGlobalAdmin = isSuperAdmin;
  const enableProjectsV2 =
    process.env.NEXT_PUBLIC_ENABLE_PROJECTSV2 !== "false";
  const normalizeParam = (value) => (Array.isArray(value) ? value[0] : value);
  const preservedModuleSideQuery = React.useMemo(() => {
    const searchParams = new URLSearchParams();
    const moduleValue = normalizeParam(router.query?.module);
    const sideValue = normalizeParam(router.query?.side);

    if (moduleValue) {
      searchParams.set("module", moduleValue);
    }
    if (sideValue) {
      searchParams.set("side", sideValue);
    }

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
  }, [router.query?.module, router.query?.side]);

  const globalAdminSection = {
    sectionKey: "globalAdmin",
    sectionLabel: t("layout.sidebar.section.globalAdmin"),
    icon: GlobalAdminIcon,
    links: [
      {
        name: t("layout.sidebar.link.dashboard"),
        href: "/admin/dashboard",
        icon: DashboardIcon,
      },
      { name: t("layout.sidebar.link.users"), href: "/admin/users", icon: UsersIcon },
      {
        name: t("layout.sidebar.link.pendingApprovals"),
        href: "/admin/pending-approvals",
        icon: UsersIcon,
      },
      {
        name: t("layout.sidebar.link.companies"),
        href: "/admin/companies",
        icon: Building2,
      },
      {
        name: t("nav.globalAdmin.reassignProjects"),
        href: "/admin/global-admin/reassign-projects",
        icon: ArrowLeftRight,
      },
      { name: t("layout.sidebar.link.i18n"), href: "/admin/i18n", icon: FileCog },
      {
        name: t("admin.translations.pageTitle"),
        href: "/admin/translations",
        icon: FileCog,
      },
      { name: t("layout.sidebar.link.referrals"), href: "/admin/referrals", icon: Handshake },
      {
        name: t("layout.sidebar.link.learningAndSupport"),
        href: "/admin/learning-and-support",
        icon: FileCog,
      },
      { name: t("layout.sidebar.link.usersGuide"), href: "/admin/user-guide", icon: BookOpen },
      {
        name: t("layout.sidebar.link.adminWhatsNew"),
        href: "/admin/whats-new",
        icon: Megaphone,
      },
      {
        name: t("layout.sidebar.link.billing"),
        href: `/admin/billing${preservedModuleSideQuery}`,
        icon: CreditCard,
      },
    ],
  };

  const welcomeSection = {
      sectionKey: "welcome",
      sectionLabel: t("layout.sidebar.section.welcome"),
      icon: Home,
      links: [
        {
          name: t("layout.sidebar.link.welcome"),
          href: "/welcome",
          icon: Home,
        },
      ],
    };

  const navItems = [
    welcomeSection,
    ...(showGlobalAdmin ? [globalAdminSection] : []),
    {
      sectionKey: "umpireAssistant",
      sectionLabel: t("layout.sidebar.section.umpireAssistant"),
      icon: UmpireAssistantIcon,
      links: [
        {
          name: t("layout.sidebar.link.uaDashboard"),
          href: "/ua/dashboard",
          icon: DashboardIcon,
        },
        ...(enableProjectsV2
          ? [
              {
                name: t("layout.sidebar.link.projects"),
                href: "/projectsv2?module=UA",
                icon: ProjectsIcon,
              },
            ]
          : []),
        {
          name: t("nav.reportingCenter"),
          href: "/reporting-center?module=UA",
          icon: FileText,
        },
        {
          name: t("layout.sidebar.link.typeOfLoss"),
          href: "/ua/type-of-loss",
          icon: TypeOfLossIcon,
        },
        {
          name: t("layout.sidebar.link.invoiceCenter"),
          href: "/invoice-center?module=UA",
          icon: InvoiceActivityIcon,
        },
      ],
    },
    {
      sectionKey: "adr",
      sectionLabel: t("layout.sidebar.section.adr"),
      icon: ADRIcon,
      links: [
        {
          name: t("layout.sidebar.link.adrDashboard"),
          href: "/adr/dashboard",
          icon: DashboardIcon,
        },
        ...(enableProjectsV2
          ? [
              {
                name: t("layout.sidebar.link.projects"),
                href: "/projectsv2?module=ADR",
                icon: ProjectsIcon,
              },
            ]
          : []),
        {
          name: t("nav.reportingCenter"),
          href: "/reporting-center?module=ADR",
          icon: FileText,
        },
        {
          name: t("layout.sidebar.link.typeOfLoss"),
          href: "/adr/type-of-loss",
          icon: TypeOfLossIcon,
        },
        {
          name: t("layout.sidebar.link.invoiceCenter"),
          href: "/invoice-center?module=ADR",
          icon: InvoiceActivityIcon,
        },
      ],
    },
    {
      sectionKey: "caseReady",
      sectionLabel: t("layout.sidebar.section.caseReady"),
      icon: ADRIcon,
      links: [
        {
          name: t("layout.sidebar.link.caseReadyDashboard"),
          href: "/cr/dashboard",
          icon: DashboardIcon,
        },
        ...(enableProjectsV2
          ? [
              {
                name: t("layout.sidebar.link.projects"),
                href: "/projectsv2?module=CR",
                icon: ProjectsIcon,
              },
            ]
          : []),
        {
          name: t("nav.reportingCenter"),
          href: "/reporting-center?module=CR",
          icon: FileText,
        },
        {
          name: t("layout.sidebar.link.typeOfLoss"),
          href: "/cr/type-of-loss",
          icon: TypeOfLossIcon,
        },
        {
          name: t("layout.sidebar.link.invoiceCenter"),
          href: "/invoice-center?module=CR",
          icon: InvoiceActivityIcon,
        },
      ],
    },
    {
      sectionKey: "settleFast",
      sectionLabel: t("layout.sidebar.section.settleFast"),
      icon: SettleFastIcon,
      links: [
        {
          name: t("layout.sidebar.link.settleFastDashboard"),
          href: "/sf/dashboard",
          icon: DashboardIcon,
        },
        ...(enableProjectsV2
          ? [
              {
                name: t("layout.sidebar.link.projects"),
                href: "/projectsv2?module=SettleFast",
                icon: ProjectsIcon,
              },
            ]
          : []),
        {
          name: t("nav.reportingCenter"),
          href: "/reporting-center?module=SettleFast",
          icon: FileText,
        },
        {
          name: t("layout.sidebar.link.typeOfLoss"),
          href: "/sf/type-of-loss",
          icon: TypeOfLossIcon,
        },
        {
          name: t("layout.sidebar.link.invoiceCenter"),
          href: "/invoice-center?module=SettleFast",
          icon: InvoiceActivityIcon,
        },
      ],
    },
    ...(isSuperAdmin || isCompanyAdmin
      ? [
          {
            sectionKey: "companyAdmin",
            sectionLabel: t("layout.sidebar.section.companyAdmin"),
            icon: CompanyAdminIcon,
            links: [
              {
                name: t("layout.sidebar.link.companyUsers"),
                href: "/admin/company-users",
                icon: UsersIcon,
              },
              {
                name: t("layout.sidebar.link.generalInvoiceSettings"),
                href: "/admin/invoice-settings",
                icon: FileCog,
              },
              {
                name: t("layout.sidebar.link.businessProfiles"),
                href: "/admin/business-profiles",
                icon: SettingsIcon,
              },
              {
                name: t("layout.sidebar.link.reassignProjects"),
                href: "/admin/projects/reassign",
                icon: ArrowLeftRight,
              },
              {
                name: t("layout.sidebar.link.auditReports"),
                href: "/admin/audit-reports",
                icon: FileText,
              },
              ...(canManageCompanyBilling
                ? [
                    {
                      name: t("layout.sidebar.link.companyBilling"),
                      href: `/admin/company-billing${preservedModuleSideQuery}`,
                      icon: CreditCard,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    {
      sectionKey: "billingSettings",
      sectionLabel: t("layout.sidebar.section.billingSettings"),
      icon: BillingSettingsIcon,
      links: [
        { name: t("layout.sidebar.link.settings"), href: "/profile", icon: SettingsIcon },
        { name: t("layout.sidebar.link.privacy"), href: "/settings/privacy", icon: SettingsIcon },
        {
          name: t("layout.sidebar.link.businessProfiles"),
          href: "/profile/business-profiles",
          icon: SettingsIcon,
        },
        {
          name: t("layout.sidebar.link.referUsers"),
          href: "/profile/refer-users",
          icon: UsersIcon,
        },
        ...(!isSuperAdmin && !isCompanyAdmin
          ? [
              {
                name: t("layout.sidebar.link.generalInvoiceSettings"),
                href: "/admin/invoice-settings",
                icon: FileCog,
              },
            ]
          : []),
        ...(!canManageCompanyBilling
          ? [
              {
                name: t("layout.sidebar.link.billing"),
                href: `/account/billing${preservedModuleSideQuery}`,
                icon: AdminIcon,
              },
            ]
          : []),
      ],
    },
    {
      sectionKey: "learningSupport",
      sectionLabel: t("layout.sidebar.section.learningSupport"),
      icon: SupportIcon,
      links: [
        { name: t("layout.sidebar.link.faqs"), href: "/learning/faqs", icon: FileCog },
        {
          name: t("layout.sidebar.link.trainingVideos"),
          href: "/learning/videos",
          icon: DashboardIcon,
        },
        { name: t("layout.sidebar.link.usersGuide"), href: "/learning/user-guide", icon: BookOpen },
        { name: t("layout.sidebar.link.whatsNew"), href: "/whats-new", icon: Megaphone },
        { name: t("layout.sidebar.link.support"), href: "/support", icon: SupportIcon },
        { name: t("layout.sidebar.link.feedback"), href: "/feedback", icon: FeedbackIcon },
      ],
    },
  ];

  const filteredNavItems = navItems.filter(({ sectionKey }) => {
    if (sectionKey === "umpireAssistant") {
      return canAccessUA;
    }
    if (sectionKey === "adr") {
      return canAccessADR;
    }
    if (sectionKey === "caseReady") {
      return canAccessCR;
    }
    if (sectionKey === "settleFast") {
      return canAccessSF;
    }
    return true;
  });

  const parseNavModule = React.useCallback((href: string) => {
    const queryString = href.split("?")[1] || "";
    return new URLSearchParams(queryString).get("module") || "";
  }, []);

  const routerModule = String(normalizeParam(router.query?.module) || "UA");

  const isLinkActive = React.useCallback(
    (linkHref: string) => {
      const linkHrefBase = linkHref.split("?")[0];
      const linkModule = parseNavModule(linkHref);
      const isSettingsLink = linkHrefBase === "/profile";
      const isModuleScopedLink =
        linkHrefBase === "/hourly-rate" ||
        linkHrefBase === "/invoice-center" ||
        linkHref.includes("module=");

      return linkHrefBase === "/projectsv2"
        ? (
            router.pathname === "/projectsv2" ||
            router.pathname.startsWith("/projects-v2")
          ) &&
            routerModule === linkModule
        : isModuleScopedLink
        ? router.pathname === linkHrefBase && routerModule === (linkModule || "UA")
        : isSettingsLink
        ? router.pathname === linkHrefBase
        : router.pathname.startsWith(linkHrefBase);
    },
    [parseNavModule, router.pathname, routerModule],
  );

  const getActiveSectionKey = React.useCallback(
    (sections) =>
      sections.find((section) =>
        section.links?.some((link) => isLinkActive(link.href)),
      )?.sectionKey,
    [isLinkActive],
  );

  const activeSectionKey = getActiveSectionKey(filteredNavItems);
  const isSectionOpen = React.useCallback(
    (sectionKey: string) => {
      const explicit = openSections[sectionKey];
      if (explicit === true) return true;
      if (explicit === false) return false;
      return sectionKey === activeSectionKey;
    },
    [activeSectionKey, openSections],
  );

  React.useEffect(() => {
    if (!activeSectionKey) return;

    setOpenSections((prev) => {
      if (prev[activeSectionKey] !== undefined) return prev;
      return { ...prev, [activeSectionKey]: true };
    });
  }, [activeSectionKey]);

  return (
    <aside
      className={`fixed top-0 md:top-16 bottom-0 left-0 ${
        isSidebarCollapsed ? "w-16" : "w-64"
      } flex flex-col transform transition-[width,transform] duration-300 ease-in-out bg-gradient-to-r from-white to-[#00A0DC] dark:from-[#4B5563] dark:to-[#1E3A8A] text-neutral-900 dark:text-neutral-100 ${
        isSidebarOpen
          ? "inset-0 right-auto z-50 translate-x-0"
          : "z-40 -translate-x-full"
      } md:inset-auto md:bottom-0 md:left-0 md:z-40 md:translate-x-0`}
    >
      {/* Navigation */}
      <nav
        className={`flex-1 overflow-y-auto ${
          isSidebarCollapsed ? "px-2 pb-24 pt-2" : "p-4 pb-24"
        }`}
      >
        {filteredNavItems.map(
          ({ sectionKey, sectionLabel, icon: Icon, links, sectionHref }, idx) => (
          <div
            key={sectionKey}
            className={`mt-6 first:mt-0 ${
              isSidebarCollapsed ? "flex flex-col items-center" : ""
            }`}
          >
            {/* Section Header */}
            {idx === 0 ? (
              <div className="relative w-full mb-2 h-10">
                <div
                  className={cn(
                    "flex items-center w-full h-10",
                    isSidebarCollapsed
                      ? "justify-center space-x-2"
                      : "justify-start space-x-1 pr-10",
                    !isSidebarCollapsed ? "-ml-2" : "",
                  )}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setOpenSections((prev) => ({
                        ...prev,
                        [sectionKey]: !isSectionOpen(sectionKey),
                      }))
                    }
                    className={cn(
                      "flex items-center justify-center rounded-md p-0.5 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900",
                    )}
                    aria-label={
                      isSectionOpen(sectionKey)
                        ? t("layout.sidebar.collapseSection")
                        : t("layout.sidebar.expandSection")
                    }
                    title={
                      isSectionOpen(sectionKey)
                        ? t("layout.sidebar.collapseSection")
                        : t("layout.sidebar.expandSection")
                    }
                  >
                    <ChevronRight
                      className={cn(
                        "w-4 h-4 transition-transform duration-200",
                        isSectionOpen(sectionKey) ? "rotate-90" : "",
                      )}
                    />
                  </button>
                  {sectionHref ? (
                    <Link
                      href={sectionHref}
                      onClick={() => setIsSidebarOpen(false)}
                      className={cn(
                        "flex items-center h-10",
                        isSidebarCollapsed
                          ? "justify-center"
                          : "flex-1 justify-start space-x-2",
                      )}
                      title={sectionLabel}
                      aria-label={sectionLabel}
                    >
                      <Icon className="w-6 h-6" />
                      <span
                        className={`uppercase text-xs font-semibold tracking-wide ${
                          isSidebarCollapsed ? "sr-only" : ""
                        }`}
                      >
                        {sectionLabel}
                      </span>
                    </Link>
                  ) : (
                    <div
                      className={cn(
                        "flex items-center h-10",
                        isSidebarCollapsed
                          ? "justify-center"
                          : "flex-1 justify-start space-x-2",
                      )}
                      title={sectionLabel}
                    >
                      <Icon className="w-6 h-6" />
                      <span
                        className={`uppercase text-xs font-semibold tracking-wide ${
                          isSidebarCollapsed ? "sr-only" : ""
                        }`}
                      >
                        {sectionLabel}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center p-2 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                  aria-label={
                    isSidebarCollapsed
                      ? t("layout.sidebar.expand")
                      : t("layout.sidebar.collapse")
                  }
                  title={
                    isSidebarCollapsed
                      ? t("layout.sidebar.expand")
                      : t("layout.sidebar.collapse")
                  }
                >
                  {isSidebarCollapsed ? (
                    <ChevronRight className="w-4 h-4" />
                  ) : (
                    <ChevronLeft className="w-4 h-4" />
                  )}
                </button>
              </div>
            ) : (
              <div
                className={cn(
                  "flex items-center mb-2",
                  isSidebarCollapsed ? "justify-center space-x-2" : "space-x-1",
                  !isSidebarCollapsed ? "-ml-2" : "",
                )}
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenSections((prev) => ({
                      ...prev,
                      [sectionKey]: !isSectionOpen(sectionKey),
                    }))
                  }
                  className={cn(
                    "flex items-center justify-center rounded-md p-0.5 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900",
                  )}
                  aria-label={
                    isSectionOpen(sectionKey)
                      ? t("layout.sidebar.collapseSection")
                      : t("layout.sidebar.expandSection")
                  }
                  title={
                    isSectionOpen(sectionKey)
                      ? t("layout.sidebar.collapseSection")
                      : t("layout.sidebar.expandSection")
                  }
                >
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 transition-transform duration-200",
                      isSectionOpen(sectionKey) ? "rotate-90" : "",
                    )}
                  />
                </button>
                {sectionHref ? (
                  <Link
                    href={sectionHref}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`flex items-center ${
                      isSidebarCollapsed ? "justify-center" : "space-x-2"
                    }`}
                    title={sectionLabel}
                    aria-label={sectionLabel}
                  >
                    <Icon className="w-6 h-6" />
                    <span
                      className={`uppercase text-xs font-semibold tracking-wide ${
                        isSidebarCollapsed ? "sr-only" : ""
                      }`}
                    >
                      {sectionLabel}
                    </span>
                  </Link>
                ) : (
                  <div
                    className={`flex items-center ${
                      isSidebarCollapsed ? "justify-center" : "space-x-2"
                    }`}
                    title={sectionLabel}
                  >
                    <Icon className="w-6 h-6" />
                    <span
                      className={`uppercase text-xs font-semibold tracking-wide ${
                        isSidebarCollapsed ? "sr-only" : ""
                      }`}
                    >
                      {sectionLabel}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Menu Items */}
            {links.length > 0 && isSectionOpen(sectionKey) ? (
              <div
                className={cn(
                  "space-y-1",
                  isSidebarCollapsed ? "flex flex-col items-center" : "pl-4",
                )}
              >
                {links.map((link) => {
                  const isActive = isLinkActive(link.href);
                  const LinkIcon = link.icon;
                  return (
                    <Link
                      key={link.name}
                      href={link.href}
                      onClick={() => setIsSidebarOpen(false)}
                      className={`flex items-center ${
                        isSidebarCollapsed ? "justify-center px-2" : "space-x-2 px-3"
                      } py-2 rounded-md transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900 ${
                        isActive
                          ? "border-l-2 border-[#00A0DC] dark:border-[#1E3A8A] bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
                      }`}
                      title={link.name}
                      aria-label={link.name}
                    >
                      <LinkIcon className="w-5 h-5" />
                      <span className={isSidebarCollapsed ? "sr-only" : ""}>
                        {link.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
        ))}
      </nav>
    </aside>
  );
}


// As a reminder, when editing files, do not remove or break other functionality unless explicitly told to do so.
